import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Users, 
  Eye, 
  Clock, 
  TrendingUp,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  Search,
  Share2,
  Mail,
  Link as LinkIcon,
  MapPin,
  ChevronDown,
  ChevronRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const COLORS = ["#1e3a8a", "#4f46e5", "#7c3aed", "#ec4899", "#f97316", "#10b981", "#06b6d4", "#8b5cf6", "#f59e0b", "#6b7280"];

const AdminAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCountry, setExpandedCountry] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  const getSourceIcon = (source) => {
    const icons = {
      "Organic Search": Search,
      "Direct": Globe,
      "Social Media": Share2,
      "Referral": LinkIcon,
      "Email": Mail
    };
    return icons[source] || Globe;
  };

  const getDeviceIcon = (device) => {
    const icons = {
      "Desktop": Monitor,
      "Mobile": Smartphone,
      "Tablet": Tablet
    };
    return icons[device] || Monitor;
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-slate-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Analytics</h1>
        <p className="text-slate-600 mt-1">Track your website performance and visitor insights.</p>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium">Weekly Visitors</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{analytics?.visitors?.toLocaleString()}</p>
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> +12.5% vs last week
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium">Page Views</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{analytics?.page_views?.toLocaleString()}</p>
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> +8.3% vs last week
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Eye className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium">Bounce Rate</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{analytics?.bounce_rate}%</p>
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> -2.1% improvement
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium">Avg. Session</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{analytics?.avg_session_duration}</p>
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> +15s increase
                  </p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Visitors Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Visitors Trend (30 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analytics?.daily_visitors?.slice(-14)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(date) => new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      stroke="#64748b"
                      fontSize={12}
                    />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: "white", 
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px"
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="visitors" 
                      stroke="#1e3a8a" 
                      strokeWidth={2}
                      dot={{ fill: "#1e3a8a" }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="page_views" 
                      stroke="#4f46e5" 
                      strokeWidth={2}
                      dot={{ fill: "#4f46e5" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Weekly Conversions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Weekly Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics?.weekly_trend?.slice(-8)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="week" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: "white", 
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px"
                      }}
                    />
                    <Bar dataKey="visitors" fill="#1e3a8a" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="conversions" fill="#4f46e5" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Bottom Row */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Top Pages */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Top Pages</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analytics?.top_pages?.map((page, index) => (
                  <div key={page.page} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-slate-100 rounded-full flex items-center justify-center text-xs font-semibold text-slate-600">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-slate-900">{page.page}</p>
                        <p className="text-xs text-slate-500">Avg: {page.avg_time}</p>
                      </div>
                    </div>
                    <span className="font-semibold text-slate-900">{page.views.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Traffic Sources */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Traffic Sources</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analytics?.traffic_sources?.map((source) => {
                  const Icon = getSourceIcon(source.source);
                  return (
                    <div key={source.source} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                          <Icon className="w-4 h-4 text-slate-600" />
                        </div>
                        <span className="font-medium text-slate-900">{source.source}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold text-slate-900">{source.percentage}%</span>
                        <p className="text-xs text-slate-500">{source.visitors.toLocaleString()} visitors</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Device Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
        >
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Device Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[150px] mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analytics?.device_breakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={60}
                      dataKey="percentage"
                      nameKey="device"
                    >
                      {analytics?.device_breakdown?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3">
                {analytics?.device_breakdown?.map((device, index) => {
                  const Icon = getDeviceIcon(device.device);
                  return (
                    <div key={device.device} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <Icon className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-700">{device.device}</span>
                      </div>
                      <span className="font-semibold">{device.percentage}%</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Visitors by Location */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0 }}
      >
        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#1e3a8a]" />
                Visitors by Location
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">Geographic distribution of your website visitors</p>
            </div>
            <Globe className="w-8 h-8 text-slate-300" />
          </CardHeader>
          <CardContent>
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Location Chart */}
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={analytics?.visitors_by_location?.slice(0, 8)} 
                    layout="vertical"
                    margin={{ left: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                    <XAxis type="number" stroke="#64748b" fontSize={12} />
                    <YAxis 
                      type="category" 
                      dataKey="country" 
                      stroke="#64748b" 
                      fontSize={12}
                      width={100}
                      tickFormatter={(value) => value.length > 12 ? value.slice(0, 12) + "..." : value}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: "white", 
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px"
                      }}
                      formatter={(value) => [`${value.toLocaleString()} visitors`, "Visitors"]}
                    />
                    <Bar dataKey="visitors" radius={[0, 4, 4, 0]}>
                      {analytics?.visitors_by_location?.slice(0, 8).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Location Table */}
              <div className="space-y-1">
                <div className="grid grid-cols-4 gap-2 text-xs font-semibold text-slate-500 pb-2 border-b px-2">
                  <span className="col-span-2">Country / State</span>
                  <span className="text-right">Visitors</span>
                  <span className="text-right">Share</span>
                </div>
                {analytics?.visitors_by_location?.map((location, index) => (
                  <div key={location.country}>
                    {/* Country Row */}
                    <div 
                      className={`grid grid-cols-4 gap-2 items-center py-2 hover:bg-slate-50 rounded-lg px-2 transition-colors cursor-pointer ${expandedCountry === location.country ? 'bg-slate-50' : ''}`}
                      onClick={() => location.states?.length > 0 && setExpandedCountry(expandedCountry === location.country ? null : location.country)}
                    >
                      <div className="col-span-2 flex items-center gap-3">
                        {location.states?.length > 0 ? (
                          expandedCountry === location.country ? 
                            <ChevronDown className="w-4 h-4 text-slate-400" /> : 
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                        ) : (
                          <span className="w-4" />
                        )}
                        <span className="text-lg">{location.flag}</span>
                        <div>
                          <p className="font-medium text-slate-900 text-sm">{location.country}</p>
                          <p className="text-xs text-slate-400">{location.code}</p>
                        </div>
                      </div>
                      <span className="text-right font-semibold text-slate-900">
                        {location.visitors.toLocaleString()}
                      </span>
                      <div className="text-right">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium" style={{
                          backgroundColor: `${COLORS[index % COLORS.length]}15`,
                          color: COLORS[index % COLORS.length]
                        }}>
                          {location.percentage}%
                        </span>
                      </div>
                    </div>
                    
                    {/* State Rows - Expandable */}
                    <AnimatePresence>
                      {expandedCountry === location.country && location.states?.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="ml-8 border-l-2 border-slate-200 pl-4 py-1 space-y-1">
                            {location.states.map((state, stateIndex) => (
                              <div 
                                key={state.state}
                                className="grid grid-cols-4 gap-2 items-center py-1.5 px-2 hover:bg-slate-100 rounded transition-colors"
                              >
                                <div className="col-span-2 flex items-center gap-2">
                                  <MapPin className="w-3 h-3 text-slate-400" />
                                  <span className="text-sm text-slate-700">{state.state}</span>
                                </div>
                                <span className="text-right text-sm text-slate-600">
                                  {state.visitors.toLocaleString()}
                                </span>
                                <div className="text-right">
                                  <span className="text-xs text-slate-500">
                                    {state.percentage}%
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default AdminAnalytics;
